import type { Document } from '@contentful/rich-text-types'
import { BLOCKS, INLINES } from '@contentful/rich-text-types'
import { useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import { useRouter } from 'next/router'
import type { FC } from 'react'
import { useMemo } from 'react'

import { AppearanceProvider } from '@segment/matcha/foundations/AppearanceContext'

import FooterCompact from '@segment/templates/FooterCompact'
import EmailCaptureForm from '@segment/templates/Forms/EmailCaptureForm'
import LogoTiles from '@segment/templates/LogoTiles'
import NavigationBrand from '@segment/templates/NavigationBrand'
import { TilesGridWrapper } from '@segment/templates/LogoTiles/LogoTiles.views'

import { getNumCharactersFromRichText } from 'views/Template/shared/utils'

import type { ContentDownloadPage } from '@segment/api/types/templates/contentDownloadPage'

import ErrorView from '@segment/next-shared/404'

import Layout from '@segment/next-shared/layout'

import ogImageJpeg from '../../shared/assets/OgDefault.jpeg'

import ContentSection from './ContentSection'
import Hero from './Hero'

const CONFIRMATION_PAGE_BASE_PATH =
  process.env.ENVIRONMENT === `local` ? `/learn-segment-content-download-confirmation` : `/learn/download/thank-you`

const disclaimer: Document = {
  data: {},
  content: [
    {
      data: {},
      content: [
        {
          data: {},
          marks: [],
          value: `We'll share a copy of this downloadable content and send you updates about Segment’s products as we continue to build the world’s leading CDP. We use your information according to our `,
          nodeType: `text`,
        },
        {
          data: {
            uri: `https://www.twilio.com/legal/privacy/`,
          },
          content: [
            {
              data: {},
              marks: [],
              value: `privacy policy`,
              nodeType: `text`,
            },
          ],
          nodeType: INLINES.HYPERLINK,
        },
        {
          data: {},
          marks: [],
          value: `. You can update your preferences at any time. `,
          nodeType: `text`,
        },
      ],
      nodeType: BLOCKS.PARAGRAPH,
    },
  ],
  nodeType: BLOCKS.DOCUMENT,
}

interface ContentDownloadProps {
  data?: ContentDownloadPage
}

const ContentDownloadView: FC<ContentDownloadProps> = ({ data }) => {
  const theme = useTheme()
  const router = useRouter()
  if (!data) return <ErrorView />

  const { hero, seo, section, cta } = data

  const sectionDescriptionNumChars = useMemo(
    () => getNumCharactersFromRichText(section.description.content),
    [section.description.content]
  )

  return (
    <AppearanceProvider appearance="twilioLight">
      <Layout
        pageTitle={seo.pageTitle}
        pageDescription={seo.pageDescription}
        pageOG={seo.ogImage?.src || ogImageJpeg}
        themeColor={theme.palette.neutrals.white[100]}
        bodyBackground={theme.palette.neutrals.white[100]}
        pageRobotsDirective={seo.robotsDirective}
      >
        <NavigationBrand />
        <Hero overline={hero.overline} heading={hero.heading} description={hero.underImageText} image={hero.image}>
          <EmailCaptureForm
            buttonText={cta.buttonText}
            customFormIdentifier={cta.customFormId}
            loadingSuccess={true}
            customDisclaimer={disclaimer}
            additionalTrackEvent="Content Downloaded"
            onSubmit={() => {
              router.push(`${CONFIRMATION_PAGE_BASE_PATH}/${cta.confirmationPagePath}`)
            }}
            enableJobTitleField={true}
          />
        </Hero>
        {sectionDescriptionNumChars > 0 && (
          <Main>
            <ContentSection heading={section.heading} description={section.description} buttonText={cta.buttonText} />
          </Main>
        )}
        <LogoWrapper>
          <LogoTiles />
        </LogoWrapper>
        <FooterCompact />
      </Layout>
    </AppearanceProvider>
  )
}

export default ContentDownloadView

const Main = styled.main`
  width: 100%;
`
const LogoWrapper = styled.div`
  background-color: ${({ theme }) => theme.palette.twilio.gray[10]};
  ${TilesGridWrapper} {
    max-width: 100%;
  }
`
